import React, { useEffect, useState } from 'react';
import * as PropTypes from 'prop-types';
import * as placeholders from './placeholders';
import './loading-placeholder.scss';

const LoadingPlaceholder = (props) => {
  let timeout = null;
  let { ready } = props;
  const [isReady, setIsReady] = useState(ready);
  const { firstLaunchOnly } = props;

  const setReady = () => {
    if (timeout) {
      window.clearTimeout(timeout);
    }
    setIsReady(true);
  };

  const setNotReady = () => {
    const { delay } = props;

    if (delay && delay > 0) {
      timeout = window.setTimeout(() => {
        setReady();
      }, delay);
    }
  };

  const getFiller = () => {
    const {
      // eslint-disable-next-line react/prop-types
      children, className, type, customPlaceholder, showLoadingAnimation, ...rest
    } = props;

    const classes = showLoadingAnimation
      ? ['loading-placeholder', className].filter((clss) => clss).join(' ')
      : className;

    if (customPlaceholder && React.isValidElement(customPlaceholder)) {
      const mergedCustomClasses = [
        // eslint-disable-next-line react/prop-types
        customPlaceholder.props.className,
        classes
      ].filter((clss) => clss).join(' ');
      return React.cloneElement(customPlaceholder, { className: mergedCustomClasses });
    } if (customPlaceholder) {
      return customPlaceholder;
    }

    const Placeholder = placeholders[type];

    // eslint-disable-next-line react/jsx-props-no-spreading
    return <Placeholder {...rest} className={classes} />;
  };

  useEffect(() => {
    if (!firstLaunchOnly && !ready) {
      setNotReady();
    } else if (ready) {
      setReady();
    }
  }, [ready]);

  return isReady ? props.children : getFiller();
};

LoadingPlaceholder.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.element
  ]).isRequired,
  /** delay in millis to wait when passing from ready to NOT ready */
  delay: PropTypes.number,
  /** pass `true` when the content is ready and `false` when it's loading */
  ready: PropTypes.bool.isRequired,
  firstLaunchOnly: PropTypes.bool,
  /** type of placeholder to use */
  type: PropTypes.oneOf(['text', 'media', 'textRow', 'rect', 'round']),
  /** number of rows displayed in 'media' and 'text' placeholders */
  rows: PropTypes.number,
  /** color of the placeholder */
  color: PropTypes.string,
  /** pass true to show a nice loading animation on the placeholder */
  showLoadingAnimation: PropTypes.bool,
  /** pass any renderable content to be used as placeholder instead of the built-in ones */
  customPlaceholder: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.element
  ]),
  className: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  style: PropTypes.object
};

LoadingPlaceholder.defaultProps = {
  delay: 0,
  type: 'text',
  color: '#CDCDCD'
};

export { LoadingPlaceholder };
